<template>
  <FormContainer class="register__personal-credential-data-form__form-container">
    <div class="personal-credential-data-form__text-block">
      <h2 class="personal-credential-data-form__title">Вітаю в UConnect</h2>
      <p class="personal-credential-data-form__description">Ми надійна CRM-платформа для агентів і партнерських мереж</p>
    </div>
    <StatusProgress
      class="personal-credential-data-form__progress"
      :current-step="getCurrentStep"
    />
    <form class="personal-credential-data__form" @submit.prevent="handleSubmit">
      <div class="personal-credential-data__input--telephone">
        <label class="input-label">Телефон</label>
        <input
          type="text"
          class="input-text"
          :class="{ 'input-text--has-error': errors.telephone }"
          placeholder="+38(0**) ***-**-**"
          v-model="phoneDisplay"
          @input="handlePhoneInput"
          @keydown="handlePhoneKeydown"
          @focus="handlePhoneFocus"
          @blur="handlePhoneBlur"
        />
      </div>
      <LabeledTextInput
        class="personal-credential-data__input--email"
        label="Email"
        placeholder="example@email.com"
        v-model="form.email"
        :error-messages="errors.email ? [errors.email] : []"
        @input="clearError('email')"
      />
      <p class="personal-credential-data__text" v-show="form.email">
        Вам прийде лист із посиланням для підтвердження на адресу {{ form.email }} для завершення реєстрації
      </p>
      <div class="personal-credential-data__next-step">
        <TurnstileWidget
          ref="turnstile"
          @token-received="handleTurnstileSuccess"
          @token-expired="handleTurnstileExpired"
        />
        <OrangeFancyButton
          class="personal-credential-data__next-step--button"
          type="submit"
          :disabled="!canSwitchToNextStep || isRegisterLoading"
        >
          <SpinnerLoader
            v-show="isRegisterLoading"
            class="personal-credential-data__controls--loader"
            color="#FFFFFF"
          />
          Далі
        </OrangeFancyButton>
        <p class="personal-credential-data__error-message" v-if="getRegisterError">{{ getRegisterError }}</p>
        <a
          class="personal-credential-data__prev-step--button"
          @click="switchPreviousStep"
        >
          Назад
        </a>
      </div>
      <div class="personal-credential-data__login">
        <p class="personal-credential-data__login-text">
          У вас вже є аккаунт?
          <SmartLink
            class="personal-credential-data__link--login"
            :route="{ name: 'login' }"
          >
            Увійти
          </SmartLink>
        </p>
      </div>
    </form>
  </FormContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StatusProgress from '../status-progress';
import { FormContainer } from '@/shared/ui/account';
import { LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';
import TurnstileWidget from '@/shared/ui/turnstile/TurnstileWidget';

export default {
  data() {
    return {
      form: {
        telephone: '',
        email: '',
      },
      phoneDisplay: '',
      errors: {
        telephone: '',
        email: '',
      },
      cfToken: '',
    };
  },
  created() {
    this.form.telephone = this.getAccountTelephone;
    this.form.email = this.getAccountEmail;
    this.phoneDisplay = this.formatPhone(this.getAccountTelephone);
  },
  components: { SpinnerLoader, FormContainer, StatusProgress, LabeledTextInput, OrangeFancyButton, SmartLink, TurnstileWidget },
  computed: {
    ...mapGetters('registerModule', [
      'getCurrentStep', 'getAccountTelephone', 'getAccountEmail', 'isRegisterLoading', 'getRegisterError',
    ]),
    canSwitchToNextStep() {
      return !!this.form.telephone && !!this.form.email && !!this.cfToken;
    },
  },
  methods: {
    ...mapActions('registerModule', [
      'switchCurrentStep', 'switchPreviousStep', 'setAccountPersonalCredentials', 'register',
    ]),
    clearError(field) {
      this.errors[field] = '';
    },
    formatPhone(value) {
      const digits = value.replace(/\D/g, '');

      const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

      let formatted = '+38 (';

      if (ukraineDigits.length > 0) {
        formatted += ukraineDigits.substring(0, 3);
      }

      if (ukraineDigits.length >= 3) {
        formatted += ') ' + ukraineDigits.substring(3, 6);
      }

      if (ukraineDigits.length >= 6) {
        formatted += '-' + ukraineDigits.substring(6, 8);
      }

      if (ukraineDigits.length >= 8) {
        formatted += '-' + ukraineDigits.substring(8, 10);
      }

      return formatted;
    },
    handlePhoneInput(event) {
      const input = event.target;
      const newValue = input.value;

      const digits = newValue.replace(/\D/g, '');

      if (digits.length <= 12) {
        this.phoneDisplay = this.formatPhone(newValue);
        this.form.telephone = digits.startsWith('38') ? digits : '38' + digits;

        this.$nextTick(() => {
          const cursorPos = this.phoneDisplay.length;
          input.setSelectionRange(cursorPos, cursorPos);
        });
      }

      this.clearError('phone');
    },
    handlePhoneKeydown(event) {
      const input = event.target;
      const cursorPosition = input.selectionStart;
      const value = input.value;

      if (event.key === 'Backspace') {
        event.preventDefault();

        if (cursorPosition <= 5) {
          return;
        }

        const digits = value.replace(/\D/g, '');
        const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

        if (ukraineDigits.length > 0) {
          const newDigits = ukraineDigits.slice(0, -1);
          this.phoneDisplay = this.formatPhone('38' + newDigits);
          this.form.telephone = '38' + newDigits;

          this.$nextTick(() => {
            const cursorPos = this.phoneDisplay.length;
            input.setSelectionRange(cursorPos, cursorPos);
          });
        }
      }

      if (event.key === 'Delete') {
        event.preventDefault();

        if (cursorPosition < 5) {
          return;
        }

        const digits = value.replace(/\D/g, '');
        const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

        if (ukraineDigits.length > 0) {
          const newDigits = ukraineDigits.slice(0, -1);
          this.phoneDisplay = this.formatPhone('38' + newDigits);
          this.form.telephone = '38' + newDigits;

          this.$nextTick(() => {
            const cursorPos = this.phoneDisplay.length;
            input.setSelectionRange(cursorPos, cursorPos);
          });
        }
      }
    },
    handlePhoneFocus(event) {
      if (!this.phoneDisplay) {
        this.phoneDisplay = '+38 (';
      }

      this.$nextTick(() => {
        if (event.target.value === '+38 (') {
          event.target.setSelectionRange(5, 5);
        }
      });
    },
    handlePhoneBlur() {
      if (this.phoneDisplay === '+38 (') {
        this.phoneDisplay = '';
        this.form.telephone = '';
      }
    },
    validatePhone(phone) {
      if (!phone || phone.trim().length === 0) {
        return 'Телефон є обов\'язковим';
      }

      const digits = phone.replace(/\D/g, '');

      if (digits.length !== 12 || !digits.startsWith('38')) {
        return 'Введіть коректний номер телефону';
      }

      return '';
    },
    validateEmail(email) {
      if (!email || email.trim().length === 0) {
        return 'Email є обов\'язковим';
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return 'Введіть коректну email адресу';
      }
      return '';
    },
    validateForm() {
      this.errors.email = this.validateEmail(this.form.email);
      this.errors.telephone = this.validatePhone(this.form.telephone);

      return !this.errors.email && !this.errors.telephone;
    },
    handleTurnstileSuccess(token) {
      this.cfToken = token;
    },
    handleTurnstileExpired() {
      this.cfToken = '';
    },
    async handleSubmit() {
      if (this.validateForm()) {
        this.setAccountPersonalCredentials({
          telephone: this.form.telephone,
          email: this.form.email,
          cfToken: this.cfToken,
        });

        try {
          await this.register();
        } catch (_) {
          if (this.$refs.turnstile) {
            this.$refs.turnstile.reset();
            this.cfToken = '';
          }
          if (this.getRegisterError) {
            return;
          }
        }

        this.switchCurrentStep();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register__personal-credential-data-form {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }
}

.personal-credential-data-form {
  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }
}

.personal-credential-data {
  &__input {
    &--telephone, &--email {
      margin-bottom: 16px;
    }
  }

  &__text {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #313957;
    margin-bottom: 32px;
  }

  &__next-step {
    margin-bottom: 32px;
  }

  &__prev-step {
    &--button {
      display: inline-block;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #313957;
      padding: 16px 24px;
      width: 100%;
      text-align: center;
      cursor: pointer;
    }
  }

  &__login {
    text-align: center;
  }

  &__link {
    &--login {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__controls {
    &--loader {
      margin-right: 12px;
    }
  }

  &__error-message {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #FF3B30;
    margin-top: 16px;
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text, .input-text {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .orange-fancy-button__content {
  display: flex;
  justify-content: center;
}
</style>
